import React, { useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  Input,
  Select,
  Button,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Card,
  CardBody,
  Text,
  IconButton,
  useBreakpointValue,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiDownload,
  FiRefreshCw,
  FiFilter,
  FiX,
} from 'react-icons/fi';
import { LoginRequestQueryParams } from '../types';

interface LoginRequestFilterBarProps {
  filters: LoginRequestQueryParams;
  onFilterChange: (filters: Partial<LoginRequestQueryParams>) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  loading?: boolean;
  totalRecords?: number;
}

const LoginRequestFilterBar: React.FC<LoginRequestFilterBarProps> = ({
  filters,
  onFilterChange,
  onRefresh,
  onExport,
  loading = false,
  totalRecords = 0,
}) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const { isOpen: isFilterOpen, onToggle: onFilterToggle } = useDisclosure();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    onFilterChange({ search: value || undefined });
  };

  const handleStatusChange = (status: string) => {
    onFilterChange({ 
      status: status === 'all' ? 'all' : status as 'pending' | 'approved' | 'rejected' 
    });
  };

  const handleClearFilters = () => {
    setSearchValue('');
    onFilterChange({
      search: undefined,
      status: 'all',
    });
  };

  const hasActiveFilters = filters.search || (filters.status && filters.status !== 'all');

  const statusOptions = [
    { value: 'all', label: 'Semua Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Disetujui' },
    { value: 'rejected', label: 'Ditolak' },
  ];

  if (isMobile) {
    return (
      <Card bg={cardBg} border="1px solid" borderColor={borderColor} mb={4}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            {/* Mobile Header */}
            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="medium">
                Filter Login Requests
              </Text>
              <HStack spacing={2}>
                <IconButton
                  aria-label="Toggle filters"
                  icon={<FiFilter />}
                  size="sm"
                  variant="ghost"
                  onClick={onFilterToggle}
                />
                {onRefresh && (
                  <IconButton
                    aria-label="Refresh"
                    icon={<FiRefreshCw />}
                    size="sm"
                    variant="ghost"
                    onClick={onRefresh}
                    isLoading={loading}
                  />
                )}
              </HStack>
            </HStack>

            {/* Mobile Search */}
            <InputGroup size="sm">
              <InputLeftElement>
                <FiSearch color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Cari berdasarkan NIP..."
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </InputGroup>

            {/* Mobile Filters */}
            <Collapse in={isFilterOpen}>
              <VStack spacing={3} align="stretch">
                <Select
                  size="sm"
                  value={filters.status || 'all'}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>

                <HStack spacing={2}>
                  {hasActiveFilters && (
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<FiX />}
                      onClick={handleClearFilters}
                      flex={1}
                    >
                      Clear
                    </Button>
                  )}
                  {onExport && (
                    <Button
                      size="sm"
                      leftIcon={<FiDownload />}
                      onClick={onExport}
                      colorScheme="blue"
                      variant="outline"
                      flex={1}
                    >
                      Export
                    </Button>
                  )}
                </HStack>
              </VStack>
            </Collapse>

            {/* Mobile Stats */}
            <Text fontSize="xs" color={textColor}>
              Total: {totalRecords.toLocaleString()} request(s)
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor} mb={6}>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* Desktop Header */}
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Text fontSize="md" fontWeight="semibold">
                Filter & Search Login Requests
              </Text>
              <Text fontSize="sm" color={textColor}>
                Total: {totalRecords.toLocaleString()} request(s)
              </Text>
            </VStack>

            <HStack spacing={2}>
              {hasActiveFilters && (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<FiX />}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              )}
              {onRefresh && (
                <Button
                  size="sm"
                  leftIcon={<FiRefreshCw />}
                  onClick={onRefresh}
                  isLoading={loading}
                  variant="outline"
                >
                  Refresh
                </Button>
              )}
              {onExport && (
                <Button
                  size="sm"
                  leftIcon={<FiDownload />}
                  onClick={onExport}
                  colorScheme="blue"
                  variant="outline"
                >
                  Export CSV
                </Button>
              )}
            </HStack>
          </HStack>

          {/* Desktop Filters */}
          <HStack spacing={4} align="end">
            {/* Search Input */}
            <Box flex={2}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Search
              </Text>
              <InputGroup>
                <InputLeftElement>
                  <FiSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Cari berdasarkan NIP, Device ID, atau Model..."
                  value={searchValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </InputGroup>
            </Box>

            {/* Status Filter */}
            <Box minW="200px">
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Status
              </Text>
              <Select
                value={filters.status || 'all'}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>
          </HStack>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default LoginRequestFilterBar;
